import type {
  ArtLabPanel,
  ArtLabState,
} from "../../../features/art-lab/art-lab-state";
import {
  affiliationsForPack,
  applyPackChange,
  ART_LAB_PACKS,
  charactersForPack,
  compositeBitsComplete,
} from "../../../features/art-lab/art-lab-state";
import { dropfrontCatalog } from "../../../data/dropfront-catalog";
import { frontlineCatalog } from "../../../data/frontline-catalog";
import { infiniteWarCatalog } from "../../../data/infinite-war-catalog";
import { portalFfaCatalog } from "../../../data/portal-ffa-catalog";
import { raidCatalog } from "../../../data/raid-catalog";
import hudPresetTable from "../../../data/tables/ui/huds/art-lab.json";
import { resolveRigConfig, type RigTables } from "../../../view/unit-rig";
import { getUiLayout } from "../../layout";
import type { HudShellHandle } from "../../layout/hud-shell";
import type { UiHudPreset } from "../../layout/types";

export type ArtLabHudHandlers = {
  onPanel: (p: ArtLabPanel) => void;
  onChange: (patch: Partial<ArtLabState>) => void;
  onPlayVfx: () => void;
  onSwing: () => void;
  onQuit: () => void;
};

const preset = hudPresetTable as UiHudPreset;

function sel(
  id: string,
  label: string,
  options: readonly string[],
  value: string,
): string {
  const opts = options
    .map(
      (o) =>
        `<option value="${o}"${o === value ? " selected" : ""}>${o}</option>`,
    )
    .join("");
  return `<label class="art-lab-field">${label}<select data-field="${id}">${opts}</select></label>`;
}

function range(
  id: string,
  label: string,
  min: number,
  max: number,
  step: number,
  value: number,
): string {
  return `<label class="art-lab-field">${label}<input data-field="${id}" type="range" min="${min}" max="${max}" step="${step}" value="${value}"/></label>`;
}

function rigTablesFor(state: ArtLabState): RigTables {
  if (state.packId === "dropfront") return dropfrontCatalog.view.rig as RigTables;
  if (state.packId === "frontline") {
    return frontlineCatalog.view.rig as RigTables;
  }
  if (state.packId === "raid") {
    return raidCatalog.view.rig as unknown as RigTables;
  }
  if (state.packId === "infinite-war") {
    return infiniteWarCatalog.view.rig as unknown as RigTables;
  }
  return portalFfaCatalog.rig as RigTables;
}

function rigKitLabel(state: ArtLabState): string {
  const cfg = resolveRigConfig(rigTablesFor(state), state.characterId);
  return `${cfg.weaponShape} · ${cfg.attackStyle}`;
}

export function mountArtLabHud(handlers: ArtLabHudHandlers): {
  sync: (state: ArtLabState) => void;
  destroy: () => void;
} {
  const ui = getUiLayout();
  ui.sync();
  const shell: HudShellHandle = ui.mountShell(preset);

  const top = shell.region("top");
  top.style.pointerEvents = "auto";
  top.innerHTML = `
    <div class="art-lab-bar">
      <strong>Art Lab</strong>
      <button type="button" data-quit>Quit</button>
    </div>
    <div class="art-lab-tabs">
      <button type="button" data-panel="body">Body</button>
      <button type="button" data-panel="rig">Rig</button>
      <button type="button" data-panel="vfx">VFX</button>
      <button type="button" data-panel="composite">Composite</button>
      <button type="button" data-panel="contract">Contract</button>
    </div>
  `;

  const bottom = shell.region("bottom");
  bottom.style.pointerEvents = "auto";

  const mid = shell.region("mid");
  mid.style.pointerEvents = "none";
  mid.innerHTML = "";

  shell.region("modal").hidden = true;
  shell.region("modal").style.pointerEvents = "none";

  top.querySelector("[data-quit]")?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    handlers.onQuit();
  });
  for (const btn of top.querySelectorAll("[data-panel]")) {
    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const p = (ev.currentTarget as HTMLElement).dataset.panel as ArtLabPanel;
      handlers.onPanel(p);
    });
  }

  const bindBottom = (state: ArtLabState) => {
    const chars = charactersForPack(state.packId);
    let html = `<div class="art-lab-controls">`;
    html += sel("packId", "pack", ART_LAB_PACKS, state.packId);
    html += sel("characterId", "character", chars, state.characterId);
    html += sel(
      "affiliation",
      "aff",
      affiliationsForPack(state.packId),
      state.affiliation,
    );

    if (state.panel === "body" || state.panel === "contract") {
      html += sel("facing", "facing", ["e", "n", "s", "w"], state.facing);
      html += sel("anim", "anim", ["idle", "walk", "attack"], state.anim);
      html += range("animTime", "time", 0, 2, 0.02, state.animTime % 2);
      html += `<label class="art-lab-field"><input data-toggle="autoPlay" type="checkbox"${state.autoPlay ? " checked" : ""}/> auto</label>`;
    }
    if (state.panel === "rig") {
      html += `<span class="art-lab-kit" title="bound to character">${rigKitLabel(state)}</span>`;
      html += range("aimDeg", "aim°", -180, 180, 1, state.aimDeg);
      html += sel("anim", "anim", ["idle", "walk", "attack"], state.anim);
      html += `<label class="art-lab-field"><input data-toggle="bobOn" type="checkbox"${state.bobOn ? " checked" : ""}/> bob</label>`;
      html += `<button type="button" data-swing>Swing</button>`;
    }
    if (state.panel === "vfx") {
      html += `<span class="art-lab-kit" title="attack kit = character">${state.characterId} kit</span>`;
      html += range(
        "vfxAngleDeg",
        "angle°",
        -180,
        180,
        1,
        state.vfxAngleDeg,
      );
      html += `<button type="button" data-play-vfx>Play 3-stage</button>`;
    }
    if (state.panel === "composite") {
      html += `<span class="art-lab-kit">${state.characterId} · body+rig+fx</span>`;
      html += range("aimDeg", "aim°", -180, 180, 1, state.aimDeg);
      html += range(
        "vfxAngleDeg",
        "angle°",
        -180,
        180,
        1,
        state.vfxAngleDeg,
      );
      html += sel("facing", "facing", ["e", "n", "s", "w"], state.facing);
      html += `<button type="button" data-play-vfx>Play attack</button>`;
      const bitLabel = `bits: body=${state.seenBodyAttack ? "✓" : "✗"} ally=${state.seenCompositeAlly ? "✓" : "✗"} enemy=${state.seenCompositeEnemy ? "✓" : "✗"}`;
      html += `<span class="art-lab-kit" title="Contract requires all bits">${bitLabel}</span>`;
    }
    if (state.panel === "contract") {
      const ok = compositeBitsComplete(state);
      html += `<span class="art-lab-kit">${ok ? "Composite bits OK" : "INCOMPLETE: play Composite ally+enemy + scrub body attack"}</span>`;
    }
    html += `</div>`;
    bottom.innerHTML = html;

    for (const el of bottom.querySelectorAll("[data-field]")) {
      el.addEventListener("change", (ev) => {
        const t = ev.target as HTMLInputElement | HTMLSelectElement;
        const field = t.dataset.field!;
        const raw = t.value;
        if (
          field === "animTime" ||
          field === "aimDeg" ||
          field === "vfxAngleDeg"
        ) {
          handlers.onChange({ [field]: Number(raw) } as Partial<ArtLabState>);
        } else if (field === "packId") {
          handlers.onChange(
            applyPackChange(state, raw as ArtLabState["packId"]),
          );
        } else {
          handlers.onChange({ [field]: raw } as Partial<ArtLabState>);
        }
      });
      el.addEventListener("input", (ev) => {
        const t = ev.target as HTMLInputElement;
        if (t.type !== "range") return;
        const field = t.dataset.field!;
        handlers.onChange({ [field]: Number(t.value) } as Partial<ArtLabState>);
      });
    }
    for (const el of bottom.querySelectorAll("[data-toggle]")) {
      el.addEventListener("change", (ev) => {
        const t = ev.target as HTMLInputElement;
        handlers.onChange({
          [t.dataset.toggle!]: t.checked,
        } as Partial<ArtLabState>);
      });
    }
    bottom.querySelector("[data-swing]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onSwing();
    });
    bottom.querySelector("[data-play-vfx]")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      handlers.onPlayVfx();
    });
  };

  let lastPanel = "";
  let lastPack = "";
  let lastChar = "";
  let lastBits = "";

  return {
    sync(state: ArtLabState) {
      for (const btn of top.querySelectorAll("[data-panel]")) {
        const p = (btn as HTMLElement).dataset.panel;
        (btn as HTMLElement).classList.toggle("is-active", p === state.panel);
      }
      const bits = `${state.seenBodyAttack}:${state.seenCompositeAlly}:${state.seenCompositeEnemy}:${state.affiliation}`;
      if (
        state.panel !== lastPanel ||
        state.packId !== lastPack ||
        state.characterId !== lastChar ||
        bits !== lastBits
      ) {
        lastPanel = state.panel;
        lastPack = state.packId;
        lastChar = state.characterId;
        lastBits = bits;
        bindBottom(state);
      } else {
        const time = bottom.querySelector(
          '[data-field="animTime"]',
        ) as HTMLInputElement | null;
        if (time && state.autoPlay) {
          time.value = String(state.animTime % 2);
        }
      }
    },
    destroy() {
      shell.destroy();
    },
  };
}
